// @ts-ignore
import Mark from 'mark.js';

import { AnchoredComment, AnchoredTextComment, CommentType } from './comment.model';

const annotationClass = (comment: AnchoredComment) => (comment.type === CommentType.PersonalNote ? 'tt-personal-note' : '');

export const getElementOffsets = (container: Element, element: Node): { start: number; length: number } => {
  try {
    const elementRange = document.createRange();

    if (element.nodeType === Node.TEXT_NODE) {
      const textNode = element as Text;
      elementRange.setStart(textNode, 0);
      elementRange.setEnd(textNode, textNode.length);
    } else {
      elementRange.selectNodeContents(element);
    }

    const preRange = elementRange.cloneRange();
    preRange.selectNodeContents(container);
    preRange.setEnd(elementRange.startContainer, elementRange.startOffset);

    const start = preRange.toString().length;
    const length = elementRange.toString().length;

    return { start, length: Math.max(0, length) };
  } catch (e) {
    return { start: 0, length: 0 };
  }
};

export const getRangeOffsets = (container: Element, range: Range): { start: number; length: number } => {
  const preRange = range.cloneRange();
  preRange.selectNodeContents(container);
  preRange.setEnd(range.startContainer, range.startOffset);

  const start = preRange.toString().length;
  const length = range.toString().length;

  return { start, length: Math.max(0, length) };
};

export const getUpdatedComment = (comment: AnchoredTextComment, container: HTMLElement) => {
  if (!comment) {
    return;
  }

  const markElements = Array.from(container.querySelectorAll<HTMLElement>(`.tt-comments-comment-${comment.id}`));

  if (markElements.length === 0) {
    return;
  }

  const topLevelMarks = markElements.filter(
    (mark) => !mark.closest(`mark.tt-comments-comment-${comment.id} mark.tt-comments-comment-${comment.id}`),
  );
  let selectedText = '';
  let start = Infinity;
  let end = -Infinity;

  topLevelMarks.forEach((mark) => {
    const text = mark.innerText.replace(/\u200B/g, '');
    if (!text.trim()) return;

    const { start: s, length } = getElementOffsets(container, mark);
    const e = s + length;
    start = Math.min(start, s);
    end = Math.max(end, e);
    selectedText += text;
  });

  selectedText = selectedText.trim();
  const length = end - start;

  if (!selectedText) return;

  return {
    ...comment,
    anchor: { ...comment.anchor, selectedContent: selectedText, start, length },
  };
};

export const markCommentOnContainer = (
  comment: AnchoredComment,
  selected: boolean,
  container: HTMLElement,
  clickCallback: (event: Event) => void,
) => {
  const parent = container.parentElement;
  if (!parent) return null;

  const existingWrapper = parent.querySelector(`.tt-comments-container`);
  if (existingWrapper) {
    return;
  }

  // Create wrapper
  const wrapper = document.createElement('div');
  wrapper.classList.add('tt-comments-wrapper');
  wrapper.style.position = 'relative';
  wrapper.addEventListener('click', clickCallback);

  // Create highlight element
  const highlight = document.createElement('div');
  const commentClass = `tt-comments-comment-${comment.id}`;
  highlight.classList.add('tt-comments-container', commentClass);
  if (annotationClass(comment)) {
    highlight.classList.add(annotationClass(comment));
  }

  if (selected) {
    highlight.classList.add('tt-comments-comment--selected');
  }

  // Insert wrapper and move container inside it
  parent.insertBefore(wrapper, container);
  wrapper.appendChild(highlight);
  wrapper.appendChild(container);

  // Adjust container stacking
  container.style.position = 'relative';
  container.style.zIndex = '1';

  return wrapper;
};

export const unmarkCommentOnContainer = (container: HTMLElement) => {
  const wrapper = container.closest('.tt-comments-wrapper');
  if (!wrapper) return;

  const grandParent = wrapper.parentElement;
  if (!grandParent) return;

  grandParent.insertBefore(container, wrapper);
  wrapper.remove();
};

export const markCommentInContainer = (comment: AnchoredComment, selected: boolean, container: HTMLElement): Promise<void> => {
  return new Promise((resolve) => {
    const instance = new Mark(container);

    const id = comment.id;
    const anchor = { ...comment.anchor };

    instance.unmark({
      className: `tt-comments-comment-${id}`,
      done: () => {
        instance.markRanges([anchor], {
          className: `tt-comments-comment-${id} ${annotationClass(comment)} ${selected ? 'tt-comments-comment--selected' : ''}`,
          done: () => {
            // Wait a bit to ensure DOM is stable after marking
            setTimeout(() => resolve(), 50);
          },
        });
      },
    });
  });
};

export const unmarkCommentInContainer = (commentId: string, container: HTMLElement) => {
  const instance = new Mark(container);
  instance.unmark({
    className: `tt-comments-comment-${commentId}`,
  });
};

export const addMutationObserverToContainer = (container: HTMLElement, callBack: () => void) => {
  let timeout: ReturnType<typeof setTimeout>;
  const observer = new MutationObserver(() => {
    if (timeout) {
      clearTimeout(timeout);
    }

    timeout = setTimeout(() => callBack(), 100);
  });

  observer.observe(container, { childList: true, characterData: true, subtree: true });

  return observer;
};
